/**
 * Jarvis クライアント（PWA）。画面は 2 つ: 認証（登録 / ログイン）と会話。状態は Gateway 側（Supabase）にあり、端末にはトークンと表示用キャッシュだけ。
 * 見た目は ADR-0055: 会話が主役。見出しの下に「今いちばん大事なこと」の 1 行。会話の流れに出るのは承認（琥珀 = 本人の判断待ち）と
 * 見張り役の短い知らせだけ。今日のこと・作業係・見張り役・昨夜の整理・声の聞き比べ・Jarvis のメモ・読み上げ・ログアウト・止めるは、
 * 下の取っ手から引き上げる「状況」の引き出しに。マイクは録った音を Mac の音声認識で文にする（ADR-0059。ブラウザの聞き取りは使わない）。
 */
import { ApiError, api, chat, store } from "./api.js";
import { renderApprovalsCard } from "./approvals.js";
import { enroll, login, logout, me, passkeysSupported } from "./auth.js";
import { renderBriefingCard } from "./briefing.js";
import type { BriefingView } from "./briefing-model.js";
import { isComposingEnter, mergeSettingsOf, UtteranceMerger } from "./compose.js";
import { deliveryReport, type ReplyDeliveryReport } from "./delivery.js";
import { mountDrawer } from "./drawer.js";
import { type Focus, focusOf } from "./focus.js";
import { calendarIcon, micIcon, ringIcon, sendIcon } from "./icons.js";
import { renderJobsCard } from "./jobs.js";
import type { JobsView } from "./jobs-model.js";
import {
  browserMicDeps,
  MIC_UNAVAILABLE,
  type MicState,
  micSettingsOf,
  RecorderInput,
} from "./mic.js";
import { renderMonitorCard, renderMonitorNotice } from "./monitor.js";
import type { MonitorView } from "./monitor-model.js";
import { renderNightlyCard } from "./nightly.js";
import { renderProposalsCard } from "./proposals.js";
import { recordLine } from "./record.js";
import { mountSelfEntry } from "./self.js";
import {
  BrowserOutput,
  describeVoice,
  GatewayVoice,
  ReplyReader,
  speechPreference,
} from "./speech.js";
import { statusLine } from "./status-model.js";
import { renderVoiceAbCard } from "./voice-ab.js";

const app = document.getElementById("app") as HTMLElement;
/** 読み上げの出力（<audio> 1 つを使い回す）と、声を作る場所（今は Gateway 経由で MacBook。ADR-0027） */
const output = new BrowserOutput();
const voice = new GatewayVoice();
/**
 * 声の入力（ADR-0059）: 録った音を Gateway → MacBook の中継 → 声の端末と同じ音声認識で文にする。
 * 会話の画面ができるまでは何もしない（onText・onState は会話の画面が差し替える）
 */
const micHandlers: { onText: (text: string) => void; onState: (s: MicState) => void } = {
  onText: () => undefined,
  onState: () => undefined,
};
const mic = new RecorderInput(browserMicDeps(), {
  onText: (t) => micHandlers.onText(t),
  onState: (s) => micHandlers.onState(s),
});

type Msg = {
  role: "user" | "assistant" | "system";
  content: string;
  trace_id?: string;
  pending?: boolean;
  /** 本人の発言で、まだ送っていない（続けて送った分とまとめる待ちの間。ADR-0040） */
  queued?: boolean;
  /** この返事でできた承認待ち（ADR-0030）。返事のすぐ下にカードを出す */
  approval_ids?: string[];
  /** 返事の途中で次の発言を送ったので、そこで止めた（ADR-0042）。「ここで止めました」の印を出す */
  interrupted?: boolean;
  /** 返事が届いた時刻（この画面で受けた返事だけ。履歴には無い） */
  at?: Date;
  /** 流した文の取り消しの数（道具の往復の印） */
  retracts?: number;
  /** 返事と一緒に届いた道具の記録（Gateway が final に添えたとき） */
  tools_used?: unknown;
};

/**
 * 流れている途中の返事（割り込みの報告と、前の返事の接続を切るのに使う。ADR-0042）。
 * 画面に出した文は msg.content、読み上げは reader
 */
interface InFlight {
  msg: Msg;
  trace_id: string | null;
  retracts: number;
  final: boolean;
  reader: ReplyReader | null;
  /** この返事の接続（次の発言の start を受けてから切る） */
  ac: AbortController;
  /** 次の発言で止めた（その後に届いた文は画面に出さない） */
  interrupted: boolean;
}

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.append(...children);
  return el;
}

function brand(): HTMLElement {
  return h(
    "div",
    { class: "jv-brand" },
    ringIcon(),
    h("div", { class: "jv-latin jv-wordmark" }, "JARVIS"),
  );
}

async function renderAuth(message = "") {
  app.replaceChildren();
  app.className = "auth";
  const support = passkeysSupported();
  const status = h(
    "p",
    { class: "muted" },
    message ||
      (support
        ? "この端末を登録するか、登録済みの端末でログインしてください。"
        : "このブラウザはパスキーに対応していません。"),
  );
  const code = h("input", {
    type: "password",
    placeholder: "登録コード（初回のみ）",
    autocomplete: "one-time-code",
    inputmode: "text",
  }) as HTMLInputElement;
  const name = h("input", {
    type: "text",
    placeholder: "端末の名前（例: iPhone）",
    value: defaultDeviceName(),
  }) as HTMLInputElement;
  const btnEnroll = h("button", { class: "primary" }, "この端末を登録") as HTMLButtonElement;
  const btnLogin = h("button", { class: "primary" }, "ログイン") as HTMLButtonElement;
  btnEnroll.disabled = !support;
  btnLogin.disabled = !support;
  btnEnroll.onclick = async () => {
    btnEnroll.disabled = true;
    try {
      await enroll(code.value.trim(), name.value.trim() || defaultDeviceName());
      await renderChat();
    } catch (e) {
      status.textContent = describe(e);
      btnEnroll.disabled = false;
    }
  };
  btnLogin.onclick = async () => {
    btnLogin.disabled = true;
    try {
      await login();
      await renderChat();
    } catch (e) {
      status.textContent = describe(e);
      btnLogin.disabled = false;
    }
  };
  app.append(
    h("header", { class: "jv-head" }, brand()),
    h(
      "section",
      { class: "card auth-card" },
      status,
      h("div", { class: "row" }, btnLogin),
      h("hr"),
      code,
      name,
      h("div", { class: "row" }, btnEnroll),
    ),
    h(
      "p",
      { class: "muted small" },
      "本人確認はこの端末のパスキー（Face ID / Touch ID）で行います。声での認識は Phase 7 で追加されます。",
    ),
  );
}

async function renderChat() {
  const who = await me();
  if (!who) return renderAuth("セッションが切れました。ログインしてください。");
  app.replaceChildren();
  app.className = "chat";

  // ── 見出し: 環と、状態の短い 1 行（緑 = ふだん、琥珀 = 本人の判断待ち）
  const statusDot = h("span", { class: "jv-dot", "aria-hidden": "true" });
  const statusText = h("span", {});
  const statusEl = h("div", { class: "jv-status", role: "status" }, statusDot, statusText);
  const facts = {
    pending_approvals: null as number | null,
    pending_proposals: null as number | null,
    frozen: null as boolean | null,
    monitor_down: false,
  };
  const drawStatus = () => {
    const s = statusLine({ ...facts, speak_on: speechPreference.on });
    statusText.textContent = s.text;
    statusEl.dataset.tone = s.tone;
    app.dataset.tone = s.tone;
  };
  const header = h("header", { class: "jv-head" }, brand(), statusEl);

  // ── 今いちばん大事なこと（1 行。無ければ出さない）
  const focusText = h("div", { class: "jv-focus-text" });
  const focusMore = h("div", { class: "jv-focus-more" });
  const focusEl = h("div", { class: "jv-focus", hidden: "" }, calendarIcon(), focusText, focusMore);
  const drawFocus = (f: Focus | null) => {
    if (!f) {
      focusEl.hidden = true;
      return;
    }
    focusEl.hidden = false;
    focusText.textContent = f.text;
    focusMore.textContent = f.more > 0 ? `ほか ${f.more} 件` : "";
    focusEl.dataset.source = f.source;
  };

  // ── 会話の流れに出るもの: 承認（ADR-0030）と見張り役の短い知らせ（ADR-0052）。ほかは引き出し
  const approvals = h("div", { class: "approvals-slot" });
  // 提案のカード（ADR-0060）。答え待ちがあるときだけ会話の中に出す（承認と同じ琥珀）。全部は引き出しの「提案」
  const proposalsInline = h("div", { class: "approvals-slot proposals-slot" });
  const notice = h("div", { class: "notice-slot" });
  const list = h("div", { class: "messages" });

  // ── 入力: 声が主、文字も同じ場所
  const input = h("textarea", {
    placeholder: "話しかける…",
    rows: "1",
    enterkeyhint: "send",
    "aria-label": "話しかける",
  }) as HTMLTextAreaElement;
  const send = h(
    "button",
    { class: "jv-send", type: "submit", "aria-label": "送信" },
    sendIcon(),
  ) as HTMLButtonElement;
  const micBtn = h(
    "button",
    { class: "jv-mic", type: "button", "aria-label": "声で話す", "aria-pressed": "false" },
    micIcon(),
  ) as HTMLButtonElement;
  const composer = h(
    "form",
    { class: "composer" },
    h("div", { class: "jv-field" }, input, send),
    micBtn,
  );
  const voiceNote = h("p", { class: "muted small voice-note", role: "status" });

  // ── 引き出し（状況）
  const drawer = mountDrawer(app, () => void openDrawer());
  app.append(
    header,
    focusEl,
    approvals,
    proposalsInline,
    notice,
    list,
    composer,
    voiceNote,
    drawer.handle,
  );

  // 引き出しの中身。各カードは自分の場所に描く（空なら枠ごと隠す）
  const section = (label: string, ...nodes: HTMLElement[]) =>
    h("section", { class: "jv-section" }, h("div", { class: "jv-section-label" }, label), ...nodes);
  const briefing = h("div", { class: "briefing-slot" });
  const jobs = h("div", { class: "jobs-slot" });
  const proposalsDrawer = h("div", { class: "proposals-drawer-slot" });
  const monitor = h("div", { class: "monitor-slot" });
  const nightly = h("div", { class: "nightly-slot" });
  const selfSlot = h("div", { class: "self-slot" });
  const approvalResults = h("div", { class: "approval-results-slot" });
  const voiceAb = h("div", { class: "voice-ab-slot" });
  const tiles = h("div", { class: "jv-tiles" });
  const secBriefing = section("今日のこと", briefing);
  const secJobs = section("作業係", jobs);
  const secProposals = section("提案", proposalsDrawer);
  const secMonitor = section("見張り役 · Jarvis の様子", tiles, monitor);
  const secNightly = section("昨夜の整理", nightly);
  const secSelf = section("Jarvis のメモ", selfSlot);
  const secApprovals = section("承認の結果", approvalResults);
  // 声の聞き比べ（ADR-0064）。待ちが無ければ出さない。押したときだけ鳴らす
  const secVoiceAb = section("声の聞き比べ", voiceAb);
  secVoiceAb.hidden = true;
  drawer.body.append(
    secBriefing,
    secProposals,
    secJobs,
    secApprovals,
    secMonitor,
    secNightly,
    secVoiceAb,
    secSelf,
  );
  // 読み上げ（ADR-0027）。既定は切。iPhone はこのボタン（と送信・マイク）の操作の中で音を出せる状態にする
  const speak = h("button", { class: "link", type: "button" }) as HTMLButtonElement;
  const drawSpeak = () => {
    const on = speechPreference.on;
    speak.textContent = on ? "読み上げ 入" : "読み上げ 切";
    speak.setAttribute("aria-pressed", String(on));
    if (!on) voiceNote.textContent = "";
    drawStatus();
  };
  speak.onclick = () => {
    speechPreference.on = !speechPreference.on;
    if (speechPreference.on) output.unlock();
    else {
      reader?.stop();
      output.stopAll();
    }
    drawSpeak();
  };
  const out = h("button", { class: "link", type: "button" }, "ログアウト");
  out.onclick = async () => {
    await logout();
    await renderAuth("ログアウトしました。");
  };
  const freezeSlot = h("div", { class: "freeze-slot" });
  drawer.foot.append(
    h("div", { class: "jv-foot-row" }, h("div", { class: "row" }, speak, out), freezeSlot),
    h(
      "p",
      { class: "muted small jv-who" },
      `${who.display_name} · 端末 ${who.device_id.slice(0, 8)} · 会話 ${store.conversationId}`,
    ),
  );
  drawSpeak();

  // 数の札（見張り役・作業係・承認待ち）。事実は各カードの読み込みから（ここで数を作らない）
  const tile = (value: string, label: string, tone = "") =>
    h(
      "div",
      { class: `jv-tile ${tone}` },
      h("div", { class: "jv-latin jv-tile-value" }, value),
      h("div", { class: "jv-tile-label" }, label),
    );
  let monitorView: MonitorView | null = null;
  let jobsView: JobsView | null = null;
  const drawTiles = () => {
    const m = monitorView;
    const mon =
      !m || m.monitor.state === "unknown" || m.monitor.state === "never"
        ? tile("—", "見張り役")
        : m.frozen
          ? tile("停止", "凍結中", "amber")
          : m.monitor.state === "down"
            ? tile("止", "見張り役が止まっています", "amber")
            : tile("OK", `見回り ${agoShort(m.monitor.last_tick_ago_sec ?? 0)}`, "ok");
    const j = jobsView;
    const running = j ? j.jobs.filter((x) => x.status === "running").length : 0;
    const worker = !j?.worker
      ? tile("—", "作業係")
      : j.worker.stale
        ? tile("?", "作業係が見えません", "amber")
        : tile(String(running), "作業係の仕事（動いている数）");
    const n = facts.pending_approvals;
    const ap =
      n === null ? tile("—", "承認待ち") : tile(String(n), "承認待ち", n > 0 ? "amber" : "");
    tiles.replaceChildren(mon, worker, ap);
  };
  const onMonitorView = (v: MonitorView) => {
    monitorView = v;
    facts.frozen = !!v.frozen;
    facts.monitor_down = v.monitor.state === "down";
    drawStatus();
    drawTiles();
  };
  const onApprovalsView = (v: { pending: number; frozen: boolean }) => {
    facts.pending_approvals = v.pending;
    if (facts.frozen === null) facts.frozen = v.frozen;
    drawStatus();
    drawTiles();
  };
  const onProposalsView = (v: { waiting: number }) => {
    facts.pending_proposals = v.waiting;
    drawStatus();
  };
  const onBriefingView = (v: BriefingView) => drawFocus(focusOf(v));
  const onJobsView = (v: JobsView) => {
    jobsView = v;
    drawTiles();
  };
  const hideIfEmpty = (sec: HTMLElement, slot: HTMLElement) => {
    sec.hidden = slot.childElementCount === 0;
  };

  const messages: Msg[] = [];
  let reader: ReplyReader | null = null;
  // 返事の下の承認のカード。描き直しで消えないよう、返事ごとに 1 つの要素を使い回す
  const replyCards = new Map<Msg, HTMLElement>();
  // 承認の答えで仕事ができることがあるので、承認のカードと一緒に作業係のカードも描き直す
  const refreshTop = () => {
    void renderApprovalsCard(approvals, { onView: onApprovalsView, show: "pending" });
    void renderProposalsCard(proposalsInline, {
      inline: true,
      onView: onProposalsView,
      onChange: refreshTop,
    });
    void renderMonitorNotice(notice, onMonitorView);
    void renderJobsCard(jobs, { onView: onJobsView }).then(() => hideIfEmpty(secJobs, jobs));
  };
  /** 引き出しを開いたとき（開くたび）に中身を描き直す */
  async function openDrawer() {
    drawer.meta.textContent = new Date().toLocaleString("ja-JP", {
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    await Promise.all([
      renderBriefingCard(briefing, { onView: onBriefingView }).then(() =>
        hideIfEmpty(secBriefing, briefing),
      ),
      renderJobsCard(jobs, { onView: onJobsView }).then(() => hideIfEmpty(secJobs, jobs)),
      renderProposalsCard(proposalsDrawer, { onView: onProposalsView, onChange: refreshTop }).then(
        () => hideIfEmpty(secProposals, proposalsDrawer),
      ),
      renderApprovalsCard(approvalResults, { show: "recent" }).then(() =>
        hideIfEmpty(secApprovals, approvalResults),
      ),
      renderMonitorCard(monitor, { onFrozen: afterFreeze, freezeSlot, onView: onMonitorView }),
      renderNightlyCard(nightly).then(() => hideIfEmpty(secNightly, nightly)),
      renderVoiceAbCard(voiceAb, { output, onRendered: () => hideIfEmpty(secVoiceAb, voiceAb) }),
      mountSelfEntry(selfSlot),
    ]);
  }
  // 止めた後は、凍結を映すよう他のカードも描き直す
  function afterFreeze() {
    refreshTop();
    void renderBriefingCard(briefing, { onView: onBriefingView }).then(() =>
      hideIfEmpty(secBriefing, briefing),
    );
  }

  /** 返事の記録の行（したこと · 時刻）。押すと trace_id を出す（不具合の報告に貼る） */
  function recordEl(m: Msg): Node | string {
    const text = recordLine({
      tools_used: m.tools_used,
      approval_ids: m.approval_ids,
      retracts: m.retracts,
      at: m.at ?? null,
      interrupted: m.interrupted,
    });
    if (!text && !m.trace_id) return "";
    const trace = h("span", { class: "trace", hidden: "" }, m.trace_id ?? "");
    const btn = h(
      "button",
      { class: "record", type: "button", title: "押すと記録の番号（trace_id）を出します" },
      text ?? "記録",
      trace,
    );
    btn.onclick = () => {
      trace.hidden = !trace.hidden;
    };
    return btn;
  }
  function draw() {
    list.replaceChildren(
      ...messages.map((m) =>
        h(
          "div",
          { class: `msg ${m.role}${m.pending ? " pending" : ""}${m.queued ? " queued" : ""}` },
          h("div", { class: "bubble" }, m.content || (m.interrupted ? "" : "…")),
          m.role === "user" ? "" : recordEl(m),
          m.approval_ids?.length ? (replyCards.get(m) ?? "") : "",
        ),
      ),
    );
    list.scrollTop = list.scrollHeight;
    nearBottom = true;
  }
  // 承認のカードや知らせが後から出て会話の欄が縮んでも、最後の発言が見えたままになるように
  let nearBottom = true;
  list.addEventListener("scroll", () => {
    nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
  });
  if (typeof ResizeObserver !== "undefined")
    new ResizeObserver(() => {
      if (nearBottom) list.scrollTop = list.scrollHeight;
    }).observe(list);
  try {
    const hist = await api<{ messages: Msg[] }>(
      `/v1/history?conversation_id=${encodeURIComponent(store.conversationId)}&limit=50`,
    );
    messages.push(...hist.messages);
  } catch {
    /* 履歴なしでも続ける */
  }
  draw();
  drawStatus();
  drawTiles();
  refreshTop();
  // 「今いちばん大事なこと」は朝のまとめから（引き出しの「今日のこと」と同じ読み込み。開く前から 1 行だけ出す）
  void renderBriefingCard(briefing, { onView: onBriefingView }).then(() =>
    hideIfEmpty(secBriefing, briefing),
  );

  // 続けて送った発言を 1 つにまとめる（ADR-0040）。待つ秒数は設定（config/client.yaml、GET /v1/me の input）
  let queued: Msg | null = null;
  // 割り込み（ADR-0042）: 流れている途中の返事と、次の発言に付ける報告
  let current: InFlight | null = null;
  let barge: { report: ReplyDeliveryReport; prior: InFlight } | null = null;

  /**
   * 返事が流れている途中・読み上げの途中で次の発言を受けたら、その場で画面と読み上げを止め、どこまで届いたかを控える。
   * 返事も読み上げも終わっていれば何もしない（報告は送らない。そのときの文脈は今までと 1 文字も変わらない）
   */
  function interruptCurrent() {
    const c = current;
    if (!c || c.interrupted) {
      reader?.stop();
      return;
    }
    const voice = c.reader && !c.reader.stopped && !c.reader.completed ? c.reader.position() : null;
    const report = deliveryReport({
      trace_id: c.trace_id,
      shown: c.msg.content,
      final: c.final,
      retracts: c.retracts,
      voice,
    });
    c.reader?.stop();
    if (!report) return;
    c.interrupted = true;
    c.msg.interrupted = true;
    c.msg.pending = false;
    // 送るのは次の発言を送るとき（続けて送った分とまとめる待ちの後）。控えるのは止めた時点の位置
    barge = { report, prior: c };
  }
  const merger = new UtteranceMerger(mergeSettingsOf(who.input), (text) => void submit(text));
  // 録音の上限（config/client.yaml の input.mic）
  mic.settings = micSettingsOf(who.input);

  /**
   * 入力欄の文を受け取る（Enter・送信のボタン・声の確定の中で呼ぶ）。now なら待たずに、それまでの分とまとめて送る。
   * 送る前から本人の吹き出しを出し、続けて送った分は同じ吹き出しに足す
   */
  function accept(now: boolean) {
    const text = input.value.trim();
    if (text) {
      // 前の返事が流れている途中・読み上げの途中なら、その場で止めて届いたところを控える（ADR-0042）。
      // iPhone では送信の操作の中で（待ちや await の前に）音を出せる状態にしておく
      if (!barge) interruptCurrent();
      else reader?.stop();
      if (speechPreference.on) output.unlock();
      input.value = "";
      composer.classList.remove("has-text");
      if (!queued) {
        queued = { role: "user", content: text, queued: true };
        messages.push(queued);
      }
      merger.add(text); // 待ちが 0 なら、ここで送る（submit が吹き出しを確定する）
      if (queued) queued.content = merger.pending ?? queued.content;
    }
    if (now) merger.flush();
    draw();
  }

  async function submit(text: string) {
    // まとめ待ちの吹き出しを、送る文で確定する
    const mine: Msg = queued ?? { role: "user", content: text };
    if (!queued) messages.push(mine);
    mine.content = text;
    mine.queued = false;
    queued = null;
    reader = speechPreference.on
      ? new ReplyReader({
          source: voice,
          out: output,
          onVoice: (s) => {
            voiceNote.textContent = describeVoice(s);
          },
          onError: (e) => {
            voiceNote.textContent = `読み上げできませんでした（${describe(e)}）。読み上げのボタンを押し直してください。`;
          },
        })
      : null;
    const r = reader;
    // 返事が流れている間も送れる（ADR-0042）。前の返事への報告があれば、この発言に付けて送る
    const b = barge;
    barge = null;
    const pending: Msg = { role: "assistant", content: "", pending: true };
    messages.push(pending);
    const self: InFlight = {
      msg: pending,
      trace_id: null,
      retracts: 0,
      final: false,
      reader: r,
      ac: new AbortController(),
      interrupted: false,
    };
    current = self;
    draw();
    try {
      const final = await chat(
        text,
        (t) => {
          if (self.interrupted) return; // 止めた後に届いた文は出さない
          pending.content = t;
          r?.partial(t);
          draw();
        },
        (trace_id, readings) => {
          self.trace_id = trace_id;
          pending.trace_id = trace_id;
          if (r) r.replyTraceId = trace_id;
          // 読み上げ用の読みは最初の知らせで届く。この返事の最初の文から中継に添える（ADR-0041）
          if (r && readings) r.readings = readings;
          // 新しい発言の start を受けてから、前の返事の接続を切る（Gateway が先に報告を記録できる順番。ADR-0042 の 2.1 の 4）
          if (b) b.prior.ac.abort();
        },
        // 流した文の取り消し（ツールの呼び出しが始まった・答え直す）。画面を消し、読み上げを止めて読み直す
        () => {
          self.retracts += 1;
          pending.retracts = self.retracts;
          if (self.interrupted) return;
          pending.content = "";
          r?.retract();
          draw();
        },
        { signal: self.ac.signal, ...(b ? { reply_delivery: b.report } : {}) },
      );
      self.final = true;
      pending.at = new Date();
      pending.tools_used = (final as { tools_used?: unknown }).tools_used;
      // 次の発言で止めた返事は、止めたところのまま（後から届いた全文で書き換えない）
      if (self.interrupted) {
        pending.trace_id = final.trace_id;
        return;
      }
      // 失敗の文（status が ok でない）は読まない
      if (final.status === "ok") r?.finish(final.reply);
      else r?.stop();
      pending.content = final.reply;
      pending.trace_id = final.trace_id;
      pending.pending = false;
      if (final.approval_ids?.length) {
        pending.approval_ids = final.approval_ids;
        const slot = h("div", { class: "reply-approvals" });
        replyCards.set(pending, slot);
        void renderApprovalsCard(slot, { only: final.approval_ids, onChange: refreshTop });
        refreshTop();
      }
      if (final.status !== "ok") pending.role = "system";
    } catch (e) {
      r?.stop();
      pending.at = new Date();
      // 次の発言で止めて接続を切った返事（ADR-0042）。誤りではない。止めたところのまま残す
      if (self.interrupted) {
        pending.pending = false;
        return;
      }
      pending.content = describe(e);
      pending.role = "system";
      pending.pending = false;
      if (e instanceof ApiError && e.status === 401) {
        await renderAuth("セッションが切れました。ログインしてください。");
        return;
      }
    } finally {
      // 返事が全部届いても、読み上げの途中なら次の発言で読み上げを止めて報告する（画面は全文、声は途中。ADR-0042 の 2.1）
      if (current === self) {
        if (self.interrupted || !r || r.stopped || r.completed) current = null;
        else
          void r.done.then(() => {
            if (current === self) current = null;
          });
      }
      draw();
      if (!mic.listening) input.focus();
    }
  }
  // 送信のボタンは待たない（それまでの分とまとめて、すぐ送る）
  composer.onsubmit = (e) => {
    e.preventDefault();
    accept(true);
  };
  input.onkeydown = (e) => {
    // 変換中の Enter（日本語の確定）では送らない。Mac の Safari は確定の Enter で isComposing が false のことがあり、keyCode 229 で見分ける
    if (e.key !== "Enter" || e.shiftKey || isComposingEnter(e)) return;
    e.preventDefault();
    accept(false);
  };
  // 打っている・変換している間は、まとめの待ちを延ばす
  input.addEventListener("input", () => {
    merger.typing();
    composer.classList.toggle("has-text", input.value.trim() !== "");
  });
  input.addEventListener("compositionstart", () => merger.typing());
  // 画面を離れるときは待たずに送る（待ちの間に閉じても、発言が消えないように）
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") merger.flush();
  });

  // 声で話す（ADR-0059）: 押して話し、もう一度押すと Mac で文にする。できた文は入力欄に出し、Enter と同じ道で送る（まとめ・割り込みはそのまま）。
  // Mac に届かないときは「Mac に届きません」と出すだけ（ブラウザの聞き取りには落ちない）
  const drawMic = () => {
    const s = mic.state;
    micBtn.classList.toggle("listening", mic.listening);
    micBtn.classList.toggle("sending", s.phase === "sending");
    micBtn.setAttribute("aria-pressed", String(mic.listening));
    micBtn.setAttribute(
      "aria-label",
      s.phase === "recording"
        ? "録音を止めて送る"
        : s.phase === "sending"
          ? "文にしています"
          : "声で話す",
    );
  };
  micHandlers.onState = (s) => {
    voiceNote.textContent = s.note;
    drawMic();
  };
  micHandlers.onText = (text) => {
    // 入力欄に打ちかけの文があれば、その後ろにつなぐ（文字と声を混ぜても 1 つの発言）
    const before = input.value.trim();
    input.value = before ? `${before}\n${text}` : text;
    composer.classList.add("has-text");
    accept(false);
  };
  micBtn.onclick = () => {
    // iPhone: 操作の中で、録音と再生の両方ができる状態にしておく（録音のため・返事の読み上げのため）。
    // 読み上げが切でも、前の読み上げで再生だけの状態になっていることがあるので、いつも切り替える
    const stopping = mic.listening;
    if (!stopping) output.unlock({ record: true });
    if (!mic.available) {
      voiceNote.textContent = MIC_UNAVAILABLE;
      return;
    }
    mic.toggle();
    // 止める押しの中で、返事の読み上げのために音を鳴らせる状態にする（録音が終わってから。再生だけに戻す）
    if (stopping && speechPreference.on) output.unlock();
  };
  // 画面を離れたら録音を捨てる（裏で録り続けない）
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") mic.cancel();
  });
  drawMic();
  if (!mic.available) micBtn.classList.add("unavailable");
}

function agoShort(sec: number): string {
  if (sec < 90) return "さっき";
  if (sec < 3600) return `${Math.round(sec / 60)} 分前`;
  if (sec < 86_400) return `${Math.round(sec / 3600)} 時間前`;
  return `${Math.round(sec / 86_400)} 日前`;
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    const map: Record<string, string> = {
      invalid_enroll_code: "登録コードが違います。",
      no_enrolled_devices: "登録済みの端末がありません。先に登録してください。",
      unknown_credential: "この端末のパスキーは登録されていません。",
      authentication_failed: "認証に失敗しました。",
      registration_failed: "登録に失敗しました。",
      unauthorized: "ログインが必要です。",
    };
    return map[e.code] ?? `エラー（${e.status} ${e.code}）`;
  }
  if (e instanceof Error && e.name === "NotAllowedError") return "キャンセルされました。";
  return e instanceof Error ? e.message : String(e);
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "端末";
}

if ("serviceWorker" in navigator && import.meta.env.PROD)
  // sw.js 自体は HTTP のキャッシュを通さずに確かめる（直した Service Worker がすぐ届くように）
  navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(() => undefined);
void (store.token ? renderChat() : renderAuth());
